import { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { video } from "shared/types/Report";
import {
    ContentShell, Panel, PanelHeader, PanelTitle, CountBadge, EmptyBadge,
    EmptyState, EmptyTitle, EmptyText, ViewerTable, ViewerHeader, ViewerBody, ViewerRow, ViewerCol,
    ViewerIndex, ViewerStrong, ViewerVideo, ViewerOnBadge, hw
} from "../homeworkUi";

interface VideoTableProps {
    list: video[]
}

const VideoPreview = ({ src, title }: { src: string; title: string }) => {
    const playerRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackError, setPlaybackError] = useState(false);

    const togglePlayback = async () => {
        const player = playerRef.current;
        if (!player) return;
        setPlaybackError(false);
        try {
            if (player.paused) {
                await player.play();
            } else {
                player.pause();
            }
        } catch {
            setPlaybackError(true);
        }
    };

    return (
        <VideoPreviewContainer>
            <ViewerVideo
                ref={playerRef}
                src={src}
                controls
                preload="metadata"
                playsInline
                aria-label={`${title} 재생`}
                onPlay={() => { setIsPlaying(true); setPlaybackError(false); }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
            />
            <PlaybackButton
                type="button"
                aria-label={`${title} 영상 ${isPlaying ? "일시정지" : "재생"}`}
                onClick={togglePlayback}
            >
                {isPlaying ? "Ⅱ 일시정지" : "▶ 재생"}
            </PlaybackButton>
            {playbackError && <PlaybackError role="alert">영상을 재생할 수 없습니다. 다시 시도해 주세요.</PlaybackError>}
        </VideoPreviewContainer>
    );
};

const VideoTableViewer = ({ list }: VideoTableProps) => {
    const renderVideoPreview = (item: video) => {
        const videoUrl = item.video_preview || (typeof item.video === "string" ? item.video : "");
        return videoUrl
            ? <VideoPreview key={videoUrl} src={videoUrl} title={item.title} />
            : <EmptyBadge>없음</EmptyBadge>;
    }

    if(!list.length){
        return (
            <ContentShell>
                <Panel>
                    <EmptyState>
                        <EmptyTitle>영상 콘텐츠가 없습니다</EmptyTitle>
                        <EmptyText>이 과제에 등록된 영상이 없거나 조회 권한이 없습니다.</EmptyText>
                    </EmptyState>
                </Panel>
            </ContentShell>
        )
    }

    return (
        <ContentShell>
            <Panel>
                <PanelHeader>
                    <PanelTitle>영상</PanelTitle>
                    <CountBadge>{list.length}개</CountBadge>
                </PanelHeader>
                <VideoTable>
                    <VideoHeader>
                        <ViewerCol>번호</ViewerCol>
                        <ViewerCol>제목</ViewerCol>
                        <ViewerCol>예문</ViewerCol>
                        <ViewerCol>영상</ViewerCol>
                        <ViewerCol>보기 전용</ViewerCol>
                    </VideoHeader>
                    <VideoBody>
                        {list.map((item, index) => (
                            <VideoRow key={index + 1}>
                                <ViewerCol><ViewerIndex>{index + 1}</ViewerIndex></ViewerCol>
                                <ViewerCol><ViewerStrong>{item.title}</ViewerStrong></ViewerCol>
                                <ViewerCol>{item.example || <EmptyBadge>없음</EmptyBadge>}</ViewerCol>
                                <ViewerCol>{renderVideoPreview(item)}</ViewerCol>
                                <ViewerCol>
                                    {item.read_only ? <ViewerOnBadge>보기 전용</ViewerOnBadge> : <EmptyBadge>학습용</EmptyBadge>}
                                </ViewerCol>
                            </VideoRow>
                        ))}
                    </VideoBody>
                </VideoTable>
            </Panel>
        </ContentShell>
    )
}

const videoColumns = css`
    display: grid;
    grid-template-columns: 56px minmax(140px, 1fr) minmax(160px, 1fr) 312px 104px;
    min-width: 772px;
`;

const VideoTable = styled(ViewerTable)`
    overflow: auto;
`;

const VideoHeader = styled(ViewerHeader)`
    ${videoColumns}
    flex-shrink: 0;
`;

const VideoBody = styled(ViewerBody)`
    overflow: visible;
    flex: 0 0 auto;
    min-height: auto;
`;

const VideoRow = styled(ViewerRow)`
    ${videoColumns}
`;

const VideoPreviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 280px;
    min-width: 280px;
    flex-shrink: 0;
`;

const PlaybackButton = styled.button`
    width: 100%;
    min-height: 36px;
    padding: 8px 12px;
    border: 1px solid ${hw.brand};
    border-radius: 8px;
    background: ${hw.brand};
    color: white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: ${hw.brandDark};
    }

    &:focus-visible {
        outline: 2px solid ${hw.brand};
        outline-offset: 2px;
    }
`;

const PlaybackError = styled.div`
    font-size: 12px;
    color: ${hw.brand};
`;

export default VideoTableViewer;
